import { hashSeed, mulberry32 } from '../../engine/math/rng';
import { PLANET_TYPES, PRESETS, SKIES } from './catalog';

/*
 * A custom universe: worlds you design in the menu's editor, joined by caves into a tree
 * (any world to any other, no loops), with one world to start in. The spec is plain data
 * (saved in the browser and carried by share links); build.ts turns it into the scenes the
 * game runs. Each world is a planet type (the lie of the land), a sky (light and air) and up
 * to four biomes sharing its ground, each setting of which can be changed.
 */

export const PLANET_TYPE_IDS = [
  'continents',
  'archipelago',
  'ocean',
  'highlands',
  'plains',
  'shattered',
  'marsh',
  'volcanic',
  'cavern',
] as const;
export type PlanetTypeId = (typeof PLANET_TYPE_IDS)[number];

export const SKY_IDS = ['sunlit', 'golden', 'pale', 'alien', 'glowing', 'ember', 'dark'] as const;
export type SkyId = (typeof SKY_IDS)[number];

export const BIOME_IDS = [
  'meadow',
  'glowmarsh',
  'lava',
  'darkstone',
  'desert',
  'savanna',
  'jungle',
  'taiga',
  'tundra',
  'snowfield',
  'badlands',
  'ashlands',
  'crystal',
  'alien',
  'autumn',
  'heath',
] as const;
export type BiomeId = (typeof BIOME_IDS)[number];

export const SPECIES_IDS = [
  'pine',
  'oak',
  'mushroom',
  'lantern',
  'crystal',
  'bone',
  'charred',
  'ember',
  'palm',
  'cactus',
  'acacia',
  'deadtree',
  'shrub',
  'drybush',
  'snowpine',
  'smallpine',
  'jungletree',
  'autumnoak',
  'goldbirch',
] as const;
export type SpeciesId = (typeof SPECIES_IDS)[number];

export const PRESET_IDS = [
  'verdant',
  'earthlike',
  'glowmire',
  'emberdeep',
  'umbral',
  'desert',
  'savanna',
  'ice',
  'jungle',
  'autumn',
  'alien',
  'ash',
  'crystal',
  'ocean',
] as const;
export type PresetId = (typeof PRESET_IDS)[number];

/** Where a biome would rather be. */
export const WHERE_IDS = ['anywhere', 'low', 'high', 'poles', 'equator'] as const;
export type WhereId = (typeof WHERE_IDS)[number];

export const MOTES_IDS = ['none', 'fireflies', 'spores', 'embers'] as const;
export type MotesId = (typeof MOTES_IDS)[number];

/** One of a world's biomes. */
export interface BiomeSlot {
  id: BiomeId;
  /** Its share of the ground (against the others'). */
  share: number;
  where: WhereId;
  /** The two tree species of its forests. */
  treeA: SpeciesId;
  treeB: SpeciesId;
}

/** Up to this many biomes share a world. */
export const MAX_BIOMES = 4;

/** One world. Colours are sRGB hex (as colour pickers give them); multipliers are 1 for the defaults. */
export interface WorldSpec {
  name: string;
  /** Radius (m). */
  radius: number;
  seed: number;
  /** Surface gravity (g); 0 for the default by size. */
  gravity: number;

  /** The lie of the land, and whether its seas are water or lava. */
  type: PlanetTypeId;
  sea: 'water' | 'lava';
  /** The light and air. */
  sky: SkyId;
  biomes: BiomeSlot[];
  /** Size of the biomes' regions, and the width of their borders (× the default). */
  biomeSize: number;
  biomeBlend: number;

  // Land: the type's recipe, scaled to the radius, then these.
  landSize: number;
  relief: number;
  /** −1 dry … 0 the type's … 1 flooded. */
  water: number;
  mountains: number;
  hills: number;
  rough: number;
  erosion: number;
  snow: boolean;
  /** Where snow settles on the meadows (m, at the type's scale). */
  snowLine: number;

  // Light
  sun: boolean;
  sunColor: string;
  sunStrength: number;
  sunSize: number;
  dayLength: number;
  daylight: number;
  duskColor: string;
  glow: number;
  glowNight: number;
  glowColor: string;
  lantern: boolean;
  startHour: number;
  exposure: number;

  // Air
  haze: number;
  clouds: number;
  cloudHeight: number;

  // Ground, all biomes alike
  groundTint: string;
  groundBrightness: number;
  groundSaturation: number;
  groundGlow: number;
  forests: number;
  forestSize: number;
  waterShallow: string;
  waterDeep: string;

  // Plants and life, all biomes alike
  grass: number;
  grassTint: string;
  trees: number;
  treeSize: number;
  foliageTint: string;
  rocks: boolean;
  birds: boolean;
  motes: MotesId;
}

/** A cave between worlds `a` and `b` (indices), through `length` metres of rock (0: automatic). */
export interface CaveSpec {
  a: number;
  b: number;
  length: number;
}

export interface UniverseSpec {
  version: 2;
  name: string;
  /** Where the worlds sit and how their caves wind. */
  seed: number;
  /** The world you start in. */
  start: number;
  worlds: WorldSpec[];
  /** Joining every world into one tree: worlds − 1 caves, no loops. */
  caves: CaveSpec[];
}

type NumberKey = { [K in keyof WorldSpec]: WorldSpec[K] extends number ? K : never }[keyof WorldSpec];

/** Allowed ranges (and slider steps) of every number. */
export const RANGES: Record<NumberKey, { min: number; max: number; step: number }> = {
  radius: { min: 200, max: 200_000, step: 10 },
  seed: { min: 0, max: 999_999, step: 1 },
  gravity: { min: 0, max: 3, step: 0.05 },
  biomeSize: { min: 0.2, max: 4, step: 0.05 },
  biomeBlend: { min: 0.2, max: 4, step: 0.05 },
  landSize: { min: 0.25, max: 4, step: 0.05 },
  relief: { min: 0, max: 3, step: 0.05 },
  water: { min: -1, max: 1, step: 0.01 },
  mountains: { min: 0, max: 3, step: 0.05 },
  hills: { min: 0, max: 4, step: 0.05 },
  rough: { min: 0, max: 4, step: 0.05 },
  erosion: { min: 0, max: 4, step: 0.05 },
  snowLine: { min: 50, max: 3000, step: 10 },
  sunStrength: { min: 0.5, max: 15, step: 0.1 },
  sunSize: { min: 0.2, max: 4, step: 0.05 },
  dayLength: { min: 30, max: 3600, step: 10 },
  daylight: { min: 0.1, max: 0.9, step: 0.01 },
  glow: { min: 0, max: 2, step: 0.01 },
  glowNight: { min: 0, max: 2, step: 0.01 },
  startHour: { min: 0, max: 24, step: 0.25 },
  exposure: { min: 0.25, max: 4, step: 0.05 },
  haze: { min: 0, max: 4, step: 0.05 },
  clouds: { min: 0, max: 1, step: 0.01 },
  cloudHeight: { min: 0.3, max: 2, step: 0.05 },
  groundBrightness: { min: 0.3, max: 2, step: 0.05 },
  groundSaturation: { min: 0, max: 2, step: 0.05 },
  groundGlow: { min: 0, max: 4, step: 0.05 },
  forests: { min: 0, max: 2, step: 0.05 },
  forestSize: { min: 0.1, max: 4, step: 0.05 },
  grass: { min: 0, max: 2.5, step: 0.05 },
  trees: { min: 0, max: 3, step: 0.05 },
  treeSize: { min: 0.4, max: 2.5, step: 0.05 },
};
/** A cave's length (m): 0 for automatic, else at least MIN_GAP (gap.ts). */
export const CAVE_LENGTH = { min: 0, max: 3000, step: 10 };

/** Worlds in one universe, at most. */
export const MAX_WORLDS = 8;
/** Below this radius a world has no air by default (as the Geodes): no haze worth drawing. */
export const SMALL_WORLD = 1000;

const WHITE = '#ffffff';

/** Each biome's own species (kept here so the spec needs no TSL to check a slot). */
const BIOME_SPECIES: Record<BiomeId, [SpeciesId, SpeciesId]> = {
  meadow: ['pine', 'oak'],
  glowmarsh: ['mushroom', 'lantern'],
  lava: ['charred', 'ember'],
  darkstone: ['crystal', 'bone'],
  desert: ['cactus', 'drybush'],
  savanna: ['acacia', 'drybush'],
  jungle: ['jungletree', 'palm'],
  taiga: ['pine', 'snowpine'],
  tundra: ['shrub', 'smallpine'],
  snowfield: ['deadtree', 'snowpine'],
  badlands: ['deadtree', 'drybush'],
  ashlands: ['charred', 'deadtree'],
  crystal: ['crystal', 'bone'],
  alien: ['mushroom', 'lantern'],
  autumn: ['autumnoak', 'goldbirch'],
  heath: ['pine', 'shrub'],
};

/** A biome slot with its biome's own tree species. */
export function biomeSlot(id: BiomeId, share = 1, where: WhereId = 'anywhere'): BiomeSlot {
  const species = BIOME_SPECIES[id];
  return { id, share, where, treeA: species[0], treeB: species[1] };
}

/** The light and air settings a sky gives a world. */
export function skyDefaults(sky: SkyId, radius: number): Partial<WorldSpec> {
  const s = SKIES[sky];
  const small = radius < SMALL_WORLD;
  return {
    sky,
    sun: s.sun !== null,
    sunColor: linearToHex(s.sun?.color ?? [1.0, 0.91, 0.78]),
    sunStrength: s.sun?.intensity ?? 5.5,
    sunSize: 1,
    dayLength: s.veil.dayLength,
    daylight: s.veil.litFraction,
    duskColor: linearToHex(s.veil.duskColor),
    glow: s.glow?.day ?? 0,
    glowNight: s.glow?.night ?? 0,
    glowColor: linearToHex(s.glow?.color ?? [0.4, 0.62, 1.0]),
    lantern: s.lantern,
    startHour: 10,
    exposure: 1,
    haze: small || !s.air ? 0 : 1,
    clouds: small ? 0 : s.clouds,
    cloudHeight: 1,
    birds: s.birds,
    motes: s.motes,
  };
}

/** The land settings a planet type gives a world. */
export function typeDefaults(type: PlanetTypeId): Partial<WorldSpec> {
  const t = PLANET_TYPES[type];
  return {
    type,
    sea: t.sea,
    landSize: 1,
    relief: 1,
    water: 0,
    mountains: 1,
    hills: 1,
    rough: 1,
    erosion: t.terrain.ridgeDamping,
    snow: t.terrain.snowLine < 1e8,
    snowLine: t.terrain.snowLine < 1e8 ? t.terrain.snowLine : 780,
    waterShallow: t.sea === 'lava' ? linearToHex([0.08, 0.02, 0.005]) : linearToHex([0.06, 0.26, 0.28]),
    waterDeep: t.sea === 'lava' ? linearToHex([0.06, 0.015, 0.004]) : linearToHex([0.01, 0.05, 0.09]),
  };
}

/** A new world made from a preset. */
export function defaultWorld(preset: PresetId, radius: number, seed: number, name: string): WorldSpec {
  const p = PRESETS[preset];
  return {
    name,
    radius,
    seed,
    gravity: 0,
    ...typeDefaults(p.type),
    ...skyDefaults(p.sky, radius),
    biomes: p.biomes.map((b) => biomeSlot(b.id, b.share, b.where)),
    biomeSize: 1,
    biomeBlend: 1,
    groundTint: WHITE,
    groundBrightness: 1,
    groundSaturation: 1,
    groundGlow: 1,
    forests: 1,
    forestSize: 1,
    grass: 1,
    grassTint: WHITE,
    trees: 1,
    treeSize: 1,
    foliageTint: WHITE,
    rocks: true,
  } as WorldSpec;
}

/** `w` made again from a preset: its name, size, seed and gravity stay. */
export function applyPreset(w: WorldSpec, preset: PresetId): WorldSpec {
  return { ...defaultWorld(preset, w.radius, w.seed, w.name), gravity: w.gravity };
}

/** The universe the editor starts with: one sunlit world. */
export function defaultUniverse(): UniverseSpec {
  return {
    version: 2,
    name: 'My worlds',
    seed: 1,
    start: 0,
    worlds: [defaultWorld('verdant', 6000, 11, 'Firsthome')],
    caves: [],
  };
}

/** Presets new worlds take in turn. */
const NEXT_PRESETS: PresetId[] = [
  'glowmire',
  'desert',
  'emberdeep',
  'ice',
  'jungle',
  'umbral',
  'alien',
  'autumn',
];

/** A world to add (a new seed, name and preset), and its cave to world `joinTo`. */
export function nextWorld(u: UniverseSpec, joinTo: number): { world: WorldSpec; cave: CaveSpec } {
  const i = u.worlds.length;
  const seed = hashSeed(u.seed, i, 7919) % 1_000_000;
  const preset = NEXT_PRESETS[(i - 1 + NEXT_PRESETS.length) % NEXT_PRESETS.length];
  const world = defaultWorld(preset, [5000, 3000, 8000, 2500][i % 4], seed, worldName(seed));
  return { world, cave: { a: joinTo, b: i, length: 0 } };
}

const NAME_A = ['Ael', 'Bry', 'Cor', 'Dun', 'Eld', 'Fen', 'Gal', 'Hal', 'Isen', 'Kel', 'Lor', 'Mor'];
const NAME_B = ['Nor', 'Os', 'Pell', 'Quen', 'Ros', 'Sil', 'Tor', 'Ul', 'Vel', 'Wyn', 'Yr', 'Zan'];
const NAME_END = [
  'mere',
  'hollow',
  'dell',
  'deep',
  'vale',
  'reach',
  'fall',
  'holm',
  'wick',
  'crag',
  'shade',
  'moor',
];

/** A name for a world, from its seed. */
export function worldName(seed: number): string {
  const r = mulberry32(seed ^ 0x5bd1e995);
  const pick = <T>(a: T[]) => a[Math.floor(r() * a.length)];
  return pick([...NAME_A, ...NAME_B]) + pick(NAME_END);
}

// --- The caves as a tree ----------------------------------------------------------------------

/** The worlds world `i` has a cave to, with the cave's index. */
export function neighbours(u: UniverseSpec, i: number): { world: number; cave: number }[] {
  const out: { world: number; cave: number }[] = [];
  u.caves.forEach((c, k) => {
    if (c.a === i) out.push({ world: c.b, cave: k });
    else if (c.b === i) out.push({ world: c.a, cave: k });
  });
  return out;
}

/**
 * Walk the tree from the start: the worlds in the order they're reached, and for each world
 * but the start the world it's reached from and the cave between (−1 for the start).
 */
export function walkFromStart(u: UniverseSpec): { order: number[]; parent: number[]; cave: number[] } {
  const parent = u.worlds.map(() => -1);
  const cave = u.worlds.map(() => -1);
  const seen = u.worlds.map((_, i) => i === u.start);
  const order = [u.start];
  for (let q = 0; q < order.length; q++) {
    for (const n of neighbours(u, order[q])) {
      if (seen[n.world]) continue;
      seen[n.world] = true;
      parent[n.world] = order[q];
      cave[n.world] = n.cave;
      order.push(n.world);
    }
  }
  return { order, parent, cave };
}

/** The caves on the way from world `x` to world `y` (indices, in order; none if they're one world). */
export function pathBetween(u: UniverseSpec, x: number, y: number): number[] {
  const back = u.worlds.map(() => -1);
  const via = u.worlds.map(() => -1);
  const seen = u.worlds.map((_, i) => i === x);
  const queue = [x];
  for (let q = 0; q < queue.length && !seen[y]; q++) {
    for (const n of neighbours(u, queue[q])) {
      if (seen[n.world]) continue;
      seen[n.world] = true;
      back[n.world] = queue[q];
      via[n.world] = n.cave;
      queue.push(n.world);
    }
  }
  const path: number[] = [];
  for (let w = y; w !== x && back[w] >= 0; w = back[w]) path.unshift(via[w]);
  return path;
}

/**
 * Join world `x` to world `y` directly, keeping a tree: the first cave on `x`'s way to `y`
 * goes, and the new cave keeps its length. So `x` leaves wherever it hung (taking the worlds
 * beyond it along), or, dragged onto a world further along its own branch, turns that
 * branch round. Nothing comes loose.
 */
export function connect(u: UniverseSpec, x: number, y: number): UniverseSpec {
  if (x === y) return u;
  const path = pathBetween(u, x, y);
  if (path.length <= 1) return u; // already joined
  const first = path[0];
  const caves = u.caves.filter((_, k) => k !== first);
  caves.push({ a: x, b: y, length: u.caves[first].length });
  return { ...u, caves };
}

/** Remove world `k`: the worlds its caves led to join the first of them instead. */
export function removeWorld(u: UniverseSpec, k: number): UniverseSpec {
  if (u.worlds.length <= 1) return u;
  const near = neighbours(u, k).map((n) => n.world);
  const hub = near[0] ?? 0;
  const caves: CaveSpec[] = u.caves.filter((c) => c.a !== k && c.b !== k).map((c) => ({ ...c }));
  for (const n of near.slice(1)) caves.push({ a: hub, b: n, length: 0 });
  const shift = (i: number) => (i > k ? i - 1 : i);
  return {
    ...u,
    start: u.start === k ? shift(hub) : shift(u.start),
    worlds: u.worlds.filter((_, i) => i !== k),
    caves: caves.map((c) => ({ ...c, a: shift(c.a), b: shift(c.b) })),
  };
}

// --- Checking what comes from storage or a link -----------------------------------------------

const HEX = /^#[0-9a-f]{6}$/i;

/** Version 1 gave each world a look; each is a preset now. */
const LOOK_PRESETS: Record<string, PresetId> = {
  verdant: 'verdant',
  glowmire: 'glowmire',
  emberdeep: 'emberdeep',
  umbral: 'umbral',
};

/**
 * A valid universe from anything (a parsed link or stored JSON, either version): unknown fields
 * dropped, the rest clamped, the caves made into one tree.
 */
export function sanitizeUniverse(raw: unknown): UniverseSpec {
  const src = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const list = Array.isArray(src.worlds) ? src.worlds.slice(0, MAX_WORLDS) : [];
  if (list.length === 0) return defaultUniverse();
  const worlds = list.map((w, i) => sanitizeWorld(w, i));
  let caves: CaveSpec[] = [];
  if (Array.isArray(src.caves)) {
    caves = src.caves.map((c) => {
      const o = (typeof c === 'object' && c !== null ? c : {}) as Record<string, unknown>;
      return {
        a: clampInt(o.a, 0, worlds.length - 1, 0),
        b: clampInt(o.b, 0, worlds.length - 1, 0),
        length: clampLength(o.length),
      };
    });
  } else {
    // Version 1: each later world's cave led to an earlier one (joinTo), its length on it.
    list.forEach((w, i) => {
      if (i === 0) return;
      const o = (typeof w === 'object' && w !== null ? w : {}) as Record<string, unknown>;
      caves.push({ a: clampInt(o.joinTo, 0, i - 1, i - 1), b: i, length: clampLength(o.caveLength) });
    });
  }
  return {
    version: 2,
    name: typeof src.name === 'string' ? src.name.slice(0, 60) : 'My worlds',
    seed: clampInt(src.seed, 0, 999_999, 1),
    start: clampInt(src.start, 0, worlds.length - 1, 0),
    worlds,
    caves: spanningTree(worlds.length, caves),
  };
}

/** Caves forming one tree over n worlds: loops and repeats dropped, loose worlds joined to world 0. */
function spanningTree(n: number, caves: CaveSpec[]): CaveSpec[] {
  const root = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => {
    while (root[i] !== i) i = root[i];
    return i;
  };
  const out: CaveSpec[] = [];
  for (const c of caves) {
    const [ra, rb] = [find(c.a), find(c.b)];
    if (c.a === c.b || ra === rb) continue;
    root[ra] = rb;
    out.push(c);
  }
  for (let i = 1; i < n; i++) {
    if (find(i) !== find(0)) {
      root[find(i)] = find(0);
      out.push({ a: 0, b: i, length: 0 });
    }
  }
  return out;
}

function sanitizeWorld(raw: unknown, index: number): WorldSpec {
  const src = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const radius = clampNum(src.radius, RANGES.radius.min, RANGES.radius.max, 6000);
  const seed = clampInt(src.seed, 0, 999_999, index + 1);
  const name = typeof src.name === 'string' && src.name.trim() ? src.name.slice(0, 40) : worldName(seed);
  // Version 1 had a look; version 2 has a type, a sky and biomes.
  const oldLook = typeof src.look === 'string' ? LOOK_PRESETS[src.look] : undefined;
  const w = defaultWorld(oldLook ?? 'verdant', radius, seed, name);
  if (PLANET_TYPE_IDS.includes(src.type as PlanetTypeId))
    Object.assign(w, typeDefaults(src.type as PlanetTypeId));
  if (SKY_IDS.includes(src.sky as SkyId)) Object.assign(w, skyDefaults(src.sky as SkyId, radius));
  if (Array.isArray(src.biomes) && src.biomes.length > 0) {
    w.biomes = src.biomes.slice(0, MAX_BIOMES).map((b) => {
      const o = (typeof b === 'object' && b !== null ? b : {}) as Record<string, unknown>;
      const id = BIOME_IDS.includes(o.id as BiomeId) ? (o.id as BiomeId) : 'meadow';
      const where = WHERE_IDS.includes(o.where as WhereId) ? (o.where as WhereId) : 'anywhere';
      const slot = biomeSlot(id, clampNum(o.share, 0.02, 1, 1), where);
      if (SPECIES_IDS.includes(o.treeA as SpeciesId)) slot.treeA = o.treeA as SpeciesId;
      if (SPECIES_IDS.includes(o.treeB as SpeciesId)) slot.treeB = o.treeB as SpeciesId;
      return slot;
    });
  } else if (oldLook) {
    // Version 1 chose the trees for the whole world.
    const slot = w.biomes[0];
    if (SPECIES_IDS.includes(src.treeA as SpeciesId)) slot.treeA = src.treeA as SpeciesId;
    if (SPECIES_IDS.includes(src.treeB as SpeciesId)) slot.treeB = src.treeB as SpeciesId;
  }
  const out = w as unknown as Record<string, unknown>;
  const skip = new Set(['name', 'radius', 'seed', 'type', 'sky', 'biomes']);
  for (const key of Object.keys(w) as (keyof WorldSpec)[]) {
    if (skip.has(key) || !(key in src)) continue;
    const v = src[key];
    const d = w[key];
    if (typeof d === 'number') {
      const r = RANGES[key as NumberKey];
      out[key] = r ? clampNum(v, r.min, r.max, d) : d;
    } else if (typeof d === 'boolean') {
      out[key] = typeof v === 'boolean' ? v : d;
    } else if (key === 'motes') {
      out[key] = MOTES_IDS.includes(v as MotesId) ? v : d;
    } else if (key === 'sea') {
      out[key] = v === 'lava' || v === 'water' ? v : d;
    } else if (typeof d === 'string') {
      out[key] = typeof v === 'string' && HEX.test(v) ? v.toLowerCase() : d;
    }
  }
  return w;
}

function clampLength(v: unknown): number {
  const n = clampNum(v, CAVE_LENGTH.min, CAVE_LENGTH.max, 0);
  return n <= 0 ? 0 : Math.max(n, 90);
}

function clampNum(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? Math.min(Math.max(n, min), max) : fallback;
}

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  return Math.round(clampNum(v, min, max, fallback));
}

// --- Saving and sharing -------------------------------------------------------------------------

const STORAGE_KEY = 'innerworlds.custom';

/** What a world is compared with for a short link: the defaults of its type and sky. */
function baseline(w: WorldSpec): Record<string, unknown> {
  const d = defaultWorld('verdant', w.radius, w.seed, w.name);
  return { ...d, ...typeDefaults(w.type), ...skyDefaults(w.sky, w.radius) } as Record<string, unknown>;
}

/**
 * A universe as a short string for a link: each world's type, sky and biomes, and only what
 * differs from their defaults, as JSON in base64url.
 */
export function encodeUniverse(u: UniverseSpec): string {
  const worlds = u.worlds.map((w) => {
    const d = baseline(w);
    const out: Record<string, unknown> = {
      name: w.name,
      radius: w.radius,
      seed: w.seed,
      type: w.type,
      sky: w.sky,
      biomes: w.biomes,
    };
    for (const [k, v] of Object.entries(w)) if (!(k in out) && d[k] !== v) out[k] = v;
    return out;
  });
  const json = JSON.stringify({ v: 2, name: u.name, seed: u.seed, start: u.start, worlds, caves: u.caves });
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** encodeUniverse's string back to a universe (null if it isn't one). */
export function decodeUniverse(s: string): UniverseSpec | null {
  try {
    const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const raw = JSON.parse(new TextDecoder().decode(bytes));
    if (!raw || !Array.isArray(raw.worlds)) return null;
    return sanitizeUniverse(raw);
  } catch {
    return null;
  }
}

/** The universe to play: from the link (`?w=`), else the one saved in this browser, else the default. */
export function loadUniverse(search = typeof location === 'undefined' ? '' : location.search): UniverseSpec {
  const fromLink = new URLSearchParams(search).get('w');
  if (fromLink) {
    const u = decodeUniverse(fromLink);
    if (u) return u;
  }
  return storedUniverse() ?? defaultUniverse();
}

/** The universe saved in this browser, if any. */
export function storedUniverse(): UniverseSpec | null {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? sanitizeUniverse(JSON.parse(s)) : null;
  } catch {
    return null;
  }
}

export function saveUniverse(u: UniverseSpec): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
  } catch {
    // storage unavailable: the link still carries it
  }
}

// --- Colours ----------------------------------------------------------------------------------

/** '#rrggbb' (sRGB) to linear RGB. */
export function hexToLinear(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  const c = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return [c((n >> 16) & 255), c((n >> 8) & 255), c(n & 255)];
}

/** Linear RGB to '#rrggbb' (sRGB), clamped to 0 … 1. */
export function linearToHex(rgb: [number, number, number]): string {
  const c = (v: number) => {
    const l = Math.min(Math.max(v, 0), 1);
    const s = l <= 0.0031308 ? l * 12.92 : 1.055 * l ** (1 / 2.4) - 0.055;
    return Math.round(s * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${c(rgb[0])}${c(rgb[1])}${c(rgb[2])}`;
}
